import apiRequest from './apiRequest';

/** Matches external Mogen API `MotionDownloadType` (GET /motion/{id} ?downloadType). */
export type MotionDownloadType = 'bvh' | 'cwms' | 'fbx' | 'gltf';
export type MotionConfigSource = 'saved' | 'temp';

export type MotionResponse = {
  motionID: string;
  status: string;
  bvhURL?: string;
  sourceBvhURL?: string;
  cwmsURL?: string;
  fbxURL?: string;
  gltfURL?: string;
  errorMessage?: string;
};

export type GetMotionPlaybackParams = {
  characterID: string;
  /** Explicit character config selector for retargeting. Omit to use the saved backend default (`config.json`). */
  configSource?: MotionConfigSource;
  /** Defaults to `bvh` (BVH retarget). Use `fbx` / `gltf` for mesh-native exports when the API supports them. */
  downloadType?: MotionDownloadType;
  /** Output frame rate for BVH/retargeted downloads (must be an allowed API value). Defaults to 30. */
  fps?: number;
  /** Hold poses every N frames (on 2s, 4s, etc.). Defaults to 1. */
  frameStep?: number;
  /**
   * Horizontal root motion (vertical preserved). Defaults to `false` when omitted so preview
   * requests match the previous client behavior; pass `true` explicitly for exports that should
   * follow API defaults.
   */
  rootMotion?: boolean;
  /**
   * Zero-based index of the person/body to retarget within a multi-person comic job.
   * Requires `characterID`. Defaults to `0` on the API when omitted.
   */
  bodyIndex?: number;
};

function buildMotionPath(
  motionID: string,
  playback?: GetMotionPlaybackParams,
): string {
  const base = `/motion/${encodeURIComponent(motionID)}`;
  if (!playback) return base;
  const rootMotion =
    playback.rootMotion !== undefined ? playback.rootMotion : false;
  const params = new URLSearchParams({
    characterID: playback.characterID,
    downloadType: playback.downloadType ?? 'bvh',
    fps: String(playback.fps ?? 30),
    frameStep: String(playback.frameStep ?? 1),
    rootMotion: String(rootMotion),
    speed: '1',
  });
  if (playback.configSource) {
    params.set('configSource', playback.configSource);
  }
  if (playback.bodyIndex !== undefined) {
    params.set('bodyIndex', String(playback.bodyIndex));
  }
  return `${base}?${params.toString()}`;
}

export default async function getMotion(
  motionID: string,
  apiKey: string,
  playback?: GetMotionPlaybackParams,
  signal?: AbortSignal,
) {
  return await apiRequest<MotionResponse>(
    'external-mogen',
    buildMotionPath(motionID, playback),
    {
      method: 'GET',
      headers: {
        'x-api-key': apiKey,
      },
    },
    signal,
  );
}
